import { AI_PRO_ADVISOR } from 'constants/AiProAdvisor';
import { AssistantActionCreatePortfolioType, AssistantActionSendEmailType } from 'types/AssistantTypes';

export interface SDKAction {
  type: string;
  id: string;
  thread_id: string;
  run_id?: string;
  input?: Record<string, unknown>;
  output?: unknown;
  status: string;
  expires_at?: string;
  created_at: string;
  updated_at?: string;
  object?: string;
  action?: {
    id: string;
    thread_id: string;
    run_id?: string;
    type: string;
    input?: Record<string, unknown>;
    output?: unknown;
    status: string;
    expires_at?: string;
    created_at: string;
    updated_at?: string;
  };
}

export interface ActionSubmissionData {
  actionId?: string;
  products?: Array<{
    weight?: string | number;
    value?: string | number;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

const PB_FORM_ALIASES: Record<string, string> = {
  fineco_pb_enter_portfolio: AI_PRO_ADVISOR.ANALYZE_PORTFOLIO_FROM_WEIGHTS,
  fineco_pb_upload_products: AI_PRO_ADVISOR.SET_PRODUCTS,
};

/**
 * Converts an SDK action to the original action format
 */
export const convertSDKActionToOriginalAction = (
  sdkAction: SDKAction,
): AssistantActionCreatePortfolioType | AssistantActionSendEmailType | null => {
  // The routed pb_* form tools render in the same forms as the native tools.
  switch (PB_FORM_ALIASES[sdkAction.type] ?? sdkAction.type) {
    case AI_PRO_ADVISOR.ANALYZE_PORTFOLIO_FROM_WEIGHTS:
      return {
        object: AI_PRO_ADVISOR.ANALYZE_PORTFOLIO_FROM_WEIGHTS,
        action: {
          id: sdkAction.id,
          thread_id: sdkAction.thread_id,
          run_id: sdkAction.run_id || '',
          type: AI_PRO_ADVISOR.ANALYZE_PORTFOLIO_FROM_WEIGHTS,
          input: sdkAction.input || {},
          output: sdkAction.output || null,
          status: sdkAction.status,
          expires_at: sdkAction.expires_at || new Date().toISOString(),
          created_at: sdkAction.created_at || new Date().toISOString(),
          updated_at: sdkAction.updated_at || new Date().toISOString(),
        },
        created_at: sdkAction.created_at,
      } as AssistantActionCreatePortfolioType;

    case AI_PRO_ADVISOR.SET_PRODUCTS:
      return {
        object: AI_PRO_ADVISOR.SET_PRODUCTS,
        action: {
          id: sdkAction.id,
          thread_id: sdkAction.thread_id,
          run_id: sdkAction.run_id || '',
          type: AI_PRO_ADVISOR.SET_PRODUCTS,
          input: sdkAction.input || {},
          output: sdkAction.output || null,
          status: sdkAction.status,
          expires_at: sdkAction.expires_at || new Date().toISOString(),
          created_at: sdkAction.created_at || new Date().toISOString(),
          updated_at: sdkAction.updated_at || new Date().toISOString(),
        },
        created_at: sdkAction.created_at,
      } as AssistantActionCreatePortfolioType;

    default:
      return null;
  }
};

/**
 * Transforms action submission data by converting string values to numbers where needed
 */
export const transformActionSubmissionData = (data: ActionSubmissionData): ActionSubmissionData => {
  const cleanPayload = { ...data };
  delete cleanPayload.actionId;

  // Transform products array if present (convert strings to numbers)
  if (cleanPayload.products) {
    cleanPayload.products = cleanPayload.products.map((product) => ({
      ...product,
      weight: typeof product.weight === 'string' ? parseFloat(product.weight) || 0 : product.weight || 0,
      value: typeof product.value === 'string' ? parseFloat(product.value) || 0 : product.value || 0,
    }));
  }

  return cleanPayload;
};
